export const BOSS_EXTENSION_ID = 'aedlocfcjgobkiejlmjgmnlomnibdoan';
export const BOSS_EXTENSION_INSTALL_GUIDE_URL =
  'https://release.baizhi.cloud/whale-hire/extensions/install.html';

export type BossExtensionUnavailableReason =
  | 'NOT_INSTALLED'
  | 'UNAVAILABLE'
  | 'TIMEOUT';

export interface BossExtensionAvailabilityResult {
  ok: boolean;
  reason?: BossExtensionUnavailableReason;
}

export const openBossExtensionInstallGuide = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.open(
    BOSS_EXTENSION_INSTALL_GUIDE_URL,
    '_blank',
    'noopener,noreferrer'
  );
};

export interface BossLoginStatusResult {
  loggedIn: boolean;
  reason?: 'NOT_LOGIN';
  raw: unknown;
}

export type BossLoginIntent = 'job_publish' | 'auto_resume';

export interface BossAutoPublishPayload {
  jobId?: string;
  attemptId?: string;
  callbackToken?: string;
  callbackUrl?: string;
  channel?: string;
  trackingCode?: string;
  location?: string;
  workType?: string;
  recruitmentType?: string;
  degree?: string;
  title?: string;
  description?: string;
  salaryMin?: number;
  salaryMax?: number;
  salaryMonths?: number;
  graduationYear?: string;
  minInternshipMonths?: number;
  minWeeklyDays?: number;
}

export interface BossAutoResumePayload {
  targetJobNames?: string[];
  timeoutMs?: number;
}

interface BossExtensionMessage {
  type:
    | 'PING'
    | 'CHECK_PLATFORM_LOGIN_STATUS'
    | 'OPEN_JOB_PUBLISH_AND_FILL'
    | 'OPEN_AUTO_RESUME_PAGE'
    | 'RUN_AUTO_RESUME'
    | 'STOP_AUTO_RESUME'
    | 'PAUSE_AUTO_RESUME'
    | 'RESUME_AUTO_RESUME';
  platform: 'boss';
  intent?: BossLoginIntent;
  payload?: BossAutoPublishPayload | BossAutoResumePayload;
}

class BossExtensionError extends Error {
  constructor(
    public reason: BossExtensionUnavailableReason,
    message: string
  ) {
    super(message);
    this.name = 'BossExtensionError';
  }
}

const getRuntime = (): ChromeRuntime.Runtime | undefined =>
  window.chrome?.runtime;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseLastErrorReason = (
  message?: string
): BossExtensionUnavailableReason => {
  if (!message) {
    return 'UNAVAILABLE';
  }

  const normalized = message.toLowerCase();
  if (
    normalized.includes('receiving end does not exist') ||
    normalized.includes('could not establish connection') ||
    normalized.includes('未找到接收端') ||
    normalized.includes('无法建立连接') ||
    normalized.includes('未检测到')
  ) {
    return 'NOT_INSTALLED';
  }

  return 'UNAVAILABLE';
};

const normalizeLoginStatus = (raw: unknown): boolean => {
  if (typeof raw === 'boolean') {
    return raw;
  }

  if (!isRecord(raw)) {
    return false;
  }

  const booleanKeys = [
    'loggedIn',
    'logged_in',
    'isLoggedIn',
    'is_logged_in',
    'isLogin',
    'is_login',
  ] as const;

  for (const key of booleanKeys) {
    const value = raw[key];
    if (typeof value === 'boolean') {
      return value;
    }
  }

  const statusKeys = ['status', 'login_status', 'auth_status'] as const;
  const loggedInStatusValues = new Set([
    'logged_in',
    'loggedin',
    'logged-in',
    'login',
    'ok',
    'authenticated',
  ]);
  const notLoggedInStatusValues = new Set([
    'not_logged_in',
    'not-logged-in',
    'unlogin',
    'unauthenticated',
    'logout',
    'logged_out',
  ]);

  for (const key of statusKeys) {
    const status = raw[key];
    if (typeof status === 'string') {
      const normalized = status.toLowerCase().trim();
      if (loggedInStatusValues.has(normalized)) {
        return true;
      }
      if (notLoggedInStatusValues.has(normalized)) {
        return false;
      }
    }
  }

  if (isRecord(raw.data)) {
    return normalizeLoginStatus(raw.data);
  }

  return false;
};

export const sendToBossExtension = async <T = unknown>(
  message: BossExtensionMessage
): Promise<T> => {
  const runtime = getRuntime();
  if (!runtime?.sendMessage) {
    throw new BossExtensionError(
      'UNAVAILABLE',
      '当前浏览器不支持扩展通信，请使用 Chrome 浏览器'
    );
  }

  return await new Promise<T>((resolve, reject) => {
    let done = false;

    const finish = (handler: () => void) => {
      if (done) {
        return;
      }
      done = true;
      handler();
    };

    try {
      runtime.sendMessage(BOSS_EXTENSION_ID, message, (response: unknown) => {
        const lastError = runtime.lastError;
        if (lastError) {
          finish(() =>
            reject(
              new BossExtensionError(
                parseLastErrorReason(lastError.message),
                lastError.message || '插件通信失败'
              )
            )
          );
          return;
        }

        finish(() => resolve(response as T));
      });
    } catch (error) {
      finish(() =>
        reject(
          new BossExtensionError(
            'UNAVAILABLE',
            error instanceof Error ? error.message : '插件通信失败'
          )
        )
      );
    }
  });
};

export const checkBossExtensionAvailable =
  async (): Promise<BossExtensionAvailabilityResult> => {
    try {
      await sendToBossExtension({
        type: 'PING',
        platform: 'boss',
        intent: 'job_publish',
      });
      return { ok: true };
    } catch (error) {
      if (error instanceof BossExtensionError) {
        return { ok: false, reason: error.reason };
      }
      return { ok: false, reason: 'UNAVAILABLE' };
    }
  };

export const checkBossLoginStatus = async (
  intent: BossLoginIntent = 'job_publish'
): Promise<BossLoginStatusResult> => {
  const raw = await sendToBossExtension({
    type: 'CHECK_PLATFORM_LOGIN_STATUS',
    platform: 'boss',
    intent,
  });

  const loggedIn = normalizeLoginStatus(raw);
  console.log('[BossExtension] checkBossLoginStatus', {
    intent,
    raw,
    normalizedLoggedIn: loggedIn,
  });
  return {
    loggedIn,
    reason: loggedIn ? undefined : 'NOT_LOGIN',
    raw,
  };
};

export const openBossPublishAndFill = async (
  payload: BossAutoPublishPayload
): Promise<unknown> => {
  return await sendToBossExtension({
    type: 'OPEN_JOB_PUBLISH_AND_FILL',
    platform: 'boss',
    payload,
  });
};

export const runBossAutoResume = async (
  payload: BossAutoResumePayload
): Promise<unknown> => {
  return await sendToBossExtension({
    type: 'RUN_AUTO_RESUME',
    platform: 'boss',
    payload,
  });
};

export const openBossAutoResumePage = async (): Promise<unknown> => {
  return await sendToBossExtension({
    type: 'OPEN_AUTO_RESUME_PAGE',
    platform: 'boss',
  });
};

export const pauseBossAutoResume = async (): Promise<unknown> => {
  return await sendToBossExtension({
    type: 'PAUSE_AUTO_RESUME',
    platform: 'boss',
  });
};

export const resumeBossAutoResume = async (): Promise<unknown> => {
  return await sendToBossExtension({
    type: 'RESUME_AUTO_RESUME',
    platform: 'boss',
  });
};

export const stopBossAutoResume = async (): Promise<unknown> => {
  return await sendToBossExtension({
    type: 'STOP_AUTO_RESUME',
    platform: 'boss',
  });
};
